import { School, ApiResponse, AcademicYears, ChannelStats } from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';
import { apiFetch } from '../config/apiClient';

class SchoolService {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async getAllSchools(academicYear?: number | null, channel?: string | null): Promise<ApiResponse<School[]>> {
    try {
      const params = new URLSearchParams();
      if (academicYear != null) params.append('academicYear', String(academicYear));
      if (channel) params.append('channel', channel);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools${query}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch schools');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch schools');
    }
  }

  async getAcademicYears(): Promise<ApiResponse<AcademicYears>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/academic-years`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch academic years');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch academic years');
    }
  }

  async getSalesChannels(): Promise<ApiResponse<string[]>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/channels`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch sales channels');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch sales channels');
    }
  }

  async getChannelStats(academicYear?: number | null): Promise<ApiResponse<ChannelStats[]>> {
    try {
      const query = academicYear != null ? `?academicYear=${academicYear}` : '';
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/channel-stats${query}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch channel stats');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch channel stats');
    }
  }

  async getSchoolById(id: string): Promise<ApiResponse<School>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch school');
    }
  }

  async createSchool(schoolData: Partial<School>): Promise<ApiResponse<School>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(schoolData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to create school');
    }
  }

  async updateSchool(id: string, schoolData: Partial<School>): Promise<ApiResponse<School>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(schoolData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update school');
    }
  }

  async deleteSchool(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete school');
    }
  }

  async searchSchools(name: string): Promise<ApiResponse<School[]>> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/search?name=${encodeURIComponent(name)}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to search schools');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to search schools');
    }
  }

  async exportSchools(academicYear?: number | null): Promise<Blob> {
    const query = academicYear != null ? `?academicYear=${academicYear}` : '';
    const response = await apiFetch(`${API_ENDPOINTS.schoolApi}/schools/export${query}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to export schools');
    }
    return await response.blob();
  }
}

const schoolService = new SchoolService();
export default schoolService;
